// src/utils/fileHash.js

/**
 * Calculate SHA-256 hash of a File object
 * @param {File} file - The file to hash
 * @returns {Promise<string>} - Promise resolving to the hash as a hex string
 */
export const calculateFileHash = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          // Convert the file to an ArrayBuffer
          const arrayBuffer = event.target.result;
          
          // Create a hash using the SubtleCrypto API
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          
          // Convert the hash to a hex string
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          
          resolve(hashHex);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      // Read the file as an ArrayBuffer
      reader.readAsArrayBuffer(file);
    });
  };
  
  /**
   * Store file hash in local storage for quick reference
   * @param {string} fileId - The file ID from the backend
   * @param {string} fileHash - The calculated file hash
   */
  export const storeFileHash = (fileId, fileHash) => {
    try {
      const hashMap = JSON.parse(localStorage.getItem('fileHashes') || '{}');
      hashMap[fileId] = fileHash;
      localStorage.setItem('fileHashes', JSON.stringify(hashMap));
    } catch (error) {
      console.error('Error storing file hash:', error);
    }
  };
  
  /**
   * Get file hash from local storage
   * @param {string} fileId - The file ID to look up
   * @returns {string|null} - The hash or null if not found
   */
  export const getStoredFileHash = (fileId) => {
    try {
      const hashMap = JSON.parse(localStorage.getItem('fileHashes') || '{}');
      return hashMap[fileId] || null;
    } catch (error) {
      console.error('Error retrieving file hash:', error);
      return null;
    }
  };